import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const HowItWorks = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const steps = [
    {
      number: "01",
      title: "Unbox & Connect",
      description: "Set up your Ainous assistant in minutes with our simple onboarding process."
    },
    {
      number: "02",
      title: "Personalize",
      description: "Tell your assistant about your preferences, routines, and needs."
    },
    {
      number: "03",
      title: "Integrate",
      description: "Connect to your smart home, apps, and services for a seamless experience."
    },
    {
      number: "04",
      title: "Experience",
      description: "Enjoy a truly personalized AI partnership that grows more helpful over time."
    }
  ];
  
  return (
    <section id="how-it-works" className="section bg-neutral-dark text-neutral-light relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-10"></div>
      <div className="absolute top-1/4 left-0 w-1/3 h-1/3 bg-primary-900 rounded-full filter blur-3xl opacity-20"></div>
      
      <div className="container mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="small-caps text-primary-400 block mb-2">Simple Process</span>
          <h2 className="mb-6">How <span className="text-gradient">Ainous</span> Works</h2>
          <p className="text-neutral-light/80">
            Getting started with your AI assistant is effortless. We've designed the entire experience to be intuitive and enjoyable.
          </p>
        </div>
        
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="glass-card bg-neutral-dark/60 p-6 border-neutral-light/10"
            >
              <div className="text-primary-400 text-4xl font-display font-bold mb-4">{step.number}</div>
              <h4 className="mb-3">{step.title}</h4>
              <p className="text-neutral-light/70">{step.description}</p>
            </motion.div>
          ))}
        </div>
        
        <div className="mt-20">
          <div className="glass-card bg-neutral-dark/60 border-neutral-light/10 p-8 md:p-12 rounded-2xl overflow-hidden">
            <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="w-full md:w-1/2">
                <h3 className="mb-6">Powered by Advanced <span className="text-gradient">AI Technology</span></h3>
                <p className="mb-6 text-neutral-light/80">
                  Behind Ainous's natural interactions is a sophisticated neural network trained on diverse data to understand context, remember details, and continuously learn from your feedback.
                </p>
                <div className="space-y-4">
                  <div className="h-2 bg-neutral-light/10 rounded-full">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={inView ? { width: "85%" } : {}}
                      transition={{ duration: 1, delay: 0.4 }}
                      className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full"
                    ></motion.div>
                  </div>
                  <div className="h-2 bg-neutral-light/10 rounded-full">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={inView ? { width: "92%" } : {}}
                      transition={{ duration: 1, delay: 0.6 }}
                      className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full"
                    ></motion.div>
                  </div>
                  <div className="h-2 bg-neutral-light/10 rounded-full">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={inView ? { width: "78%" } : {}}
                      transition={{ duration: 1, delay: 0.8 }}
                      className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full"
                    ></motion.div>
                  </div>
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6 }}
                  className="rounded-xl overflow-hidden"
                >
                  {/* Foundation Agent Architecture Diagram SVG */}
                  <svg 
                    viewBox="0 0 900 600" 
                    className="w-full h-auto" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="100%" height="100%" fill="#121212" />
                    
                    {/* Background Grid */}
                    <pattern id="grid" width="30" height="30" patternUnits="userSpaceOnUse">
                      <path d="M 30 0 L 0 0 0 30" fill="none" stroke="#ffffff" strokeOpacity="0.05" strokeWidth="1"/>
                    </pattern>
                    <rect width="100%" height="100%" fill="url(#grid)" />
                    
                    {/* Glow Effects */}
                    <defs>
                      <radialGradient id="glow1" cx="0.5" cy="0.5" r="0.5" fx="0.5" fy="0.5">
                        <stop offset="0%" stopColor="#ec4899" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#ec4899" stopOpacity="0" />
                      </radialGradient>
                      <radialGradient id="glow2" cx="0.5" cy="0.5" r="0.5" fx="0.5" fy="0.5">
                        <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0" />
                      </radialGradient>
                    </defs>
                    
                    <circle cx="250" cy="150" r="150" fill="url(#glow1)" opacity="0.6" />
                    <circle cx="650" cy="450" r="150" fill="url(#glow2)" opacity="0.6" />
                    
                    {/* Central Foundation Model */}
                    <g transform="translate(450, 300)">
                      <circle cx="0" cy="0" r="80" fill="none" stroke="url(#grad1)" strokeWidth="2" />
                      <circle cx="0" cy="0" r="76" fill="rgba(236, 72, 153, 0.1)" />
                      <text x="0" y="0" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontFamily="Lexend, sans-serif" fontSize="18" fontWeight="600">Foundation</text>
                      <text x="0" y="25" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontFamily="Lexend, sans-serif" fontSize="18" fontWeight="600">Model</text>
                    </g>
                    
                    {/* Connecting Components */}
                    <defs>
                      <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#ec4899" />
                        <stop offset="100%" stopColor="#0ea5e9" />
                      </linearGradient>
                    </defs>
                    
                    {/* Perception Module */}
                    <g transform="translate(270, 150)">
                      <rect x="-70" y="-40" width="140" height="80" rx="10" fill="rgba(14, 165, 233, 0.1)" stroke="url(#grad1)" strokeWidth="2" />
                      <text x="0" y="0" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontFamily="Lexend, sans-serif" fontSize="16" fontWeight="500">Perception</text>
                      <text x="0" y="25" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontFamily="Lexend, sans-serif" fontSize="12" fontWeight="400">Vision, Audio, Text</text>
                    </g>
                    
                    {/* Memory Module */}
                    <g transform="translate(630, 150)">
                      <rect x="-70" y="-40" width="140" height="80" rx="10" fill="rgba(14, 165, 233, 0.1)" stroke="url(#grad1)" strokeWidth="2" />
                      <text x="0" y="0" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontFamily="Lexend, sans-serif" fontSize="16" fontWeight="500">Memory</text>
                      <text x="0" y="25" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontFamily="Lexend, sans-serif" fontSize="12" fontWeight="400">Short/Long Term</text>
                    </g>
                    
                    {/* Planning Module */}
                    <g transform="translate(270, 450)">
                      <rect x="-70" y="-40" width="140" height="80" rx="10" fill="rgba(236, 72, 153, 0.1)" stroke="url(#grad1)" strokeWidth="2" />
                      <text x="0" y="0" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontFamily="Lexend, sans-serif" fontSize="16" fontWeight="500">Planning</text>
                      <text x="0" y="25" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontFamily="Lexend, sans-serif" fontSize="12" fontWeight="400">Goals & Strategies</text>
                    </g>
                    
                    {/* Action Module */}
                    <g transform="translate(630, 450)">
                      <rect x="-70" y="-40" width="140" height="80" rx="10" fill="rgba(236, 72, 153, 0.1)" stroke="url(#grad1)" strokeWidth="2" />
                      <text x="0" y="0" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontFamily="Lexend, sans-serif" fontSize="16" fontWeight="500">Action</text>
                      <text x="0" y="25" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontFamily="Lexend, sans-serif" fontSize="12" fontWeight="400">Execution & Control</text>
                    </g>
                    
                    {/* Connecting Lines - Fixed Coordinates */}
                    {/* Perception to Foundation */}
                    <line x1="340" y1="170" x2="395" y2="245" stroke="url(#grad1)" strokeWidth="2" strokeOpacity="0.8">
                      <animate attributeName="strokeOpacity" values="0.5;1;0.5" dur="3s" repeatCount="indefinite" />
                    </line>
                    {/* Memory to Foundation */}
                    <line x1="560" y1="170" x2="505" y2="245" stroke="url(#grad1)" strokeWidth="2" strokeOpacity="0.8">
                      <animate attributeName="strokeOpacity" values="0.5;1;0.5" dur="3s" repeatCount="indefinite" />
                    </line>
                    {/* Planning to Foundation */}
                    <line x1="340" y1="430" x2="395" y2="355" stroke="url(#grad1)" strokeWidth="2" strokeOpacity="0.8">
                      <animate attributeName="strokeOpacity" values="0.5;1;0.5" dur="3s" repeatCount="indefinite" />
                    </line>
                    {/* Action to Foundation */}
                    <line x1="560" y1="430" x2="505" y2="355" stroke="url(#grad1)" strokeWidth="2" strokeOpacity="0.8">
                      <animate attributeName="strokeOpacity" values="0.5;1;0.5" dur="3s" repeatCount="indefinite" />
                    </line>
                    
                    {/* Additional Cross-connections for better architecture representation */}
                    {/* Perception to Memory (top) */}
                    <line x1="340" y1="150" x2="560" y2="150" stroke="url(#grad1)" strokeWidth="1" strokeOpacity="0.4" strokeDasharray="5,5">
                      <animate attributeName="strokeOpacity" values="0.2;0.6;0.2" dur="4s" repeatCount="indefinite" />
                    </line>
                    {/* Planning to Action (bottom) */}
                    <line x1="340" y1="450" x2="560" y2="450" stroke="url(#grad1)" strokeWidth="1" strokeOpacity="0.4" strokeDasharray="5,5">
                      <animate attributeName="strokeOpacity" values="0.2;0.6;0.2" dur="4s" repeatCount="indefinite" />
                    </line>
                    
                    {/* Data Flow Particles - Enhanced Visibility */}
                    <circle cx="0" cy="0" r="4" fill="#ec4899" fillOpacity="0.9" stroke="#ec4899" strokeWidth="1">
                      <animateMotion path="M340,170 C365,200 380,220 395,245" dur="2.5s" repeatCount="indefinite" />
                      <animate attributeName="r" values="4;6;4" dur="1s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="0" cy="0" r="4" fill="#0ea5e9" fillOpacity="0.9" stroke="#0ea5e9" strokeWidth="1">
                      <animateMotion path="M560,170 C535,200 520,220 505,245" dur="2.5s" repeatCount="indefinite" begin="0.5s" />
                      <animate attributeName="r" values="4;6;4" dur="1s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="0" cy="0" r="4" fill="#ec4899" fillOpacity="0.9" stroke="#ec4899" strokeWidth="1">
                      <animateMotion path="M395,355 C380,380 365,400 340,430" dur="2.5s" repeatCount="indefinite" begin="1s" />
                      <animate attributeName="r" values="4;6;4" dur="1s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="0" cy="0" r="4" fill="#0ea5e9" fillOpacity="0.9" stroke="#0ea5e9" strokeWidth="1">
                      <animateMotion path="M505,355 C520,380 535,400 560,430" dur="2.5s" repeatCount="indefinite" begin="1.5s" />
                      <animate attributeName="r" values="4;6;4" dur="1s" repeatCount="indefinite" />
                    </circle>
                    
                    {/* Bidirectional flow particles - Enhanced */}
                    <circle cx="0" cy="0" r="3" fill="#22d3ee" fillOpacity="1" stroke="#22d3ee" strokeWidth="1">
                      <animateMotion path="M340,150 C400,150 460,150 560,150" dur="4s" repeatCount="indefinite" />
                      <animate attributeName="fillOpacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="0" cy="0" r="3" fill="#a855f7" fillOpacity="1" stroke="#a855f7" strokeWidth="1">
                      <animateMotion path="M560,450 C500,450 440,450 340,450" dur="4s" repeatCount="indefinite" begin="2s" />
                      <animate attributeName="fillOpacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" />
                    </circle>
                    
                    {/* Additional pulse particles for more dynamic effect */}
                    <circle cx="0" cy="0" r="2" fill="#ffffff" fillOpacity="0.8">
                      <animateMotion path="M340,170 C365,200 380,220 395,245" dur="3s" repeatCount="indefinite" begin="1.25s" />
                      <animate attributeName="fillOpacity" values="0;0.8;0" dur="1.5s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="0" cy="0" r="2" fill="#ffffff" fillOpacity="0.8">
                      <animateMotion path="M560,170 C535,200 520,220 505,245" dur="3s" repeatCount="indefinite" begin="1.75s" />
                      <animate attributeName="fillOpacity" values="0;0.8;0" dur="1.5s" repeatCount="indefinite" />
                    </circle>
                    
                    {/* Labels */}
                    <text x="450" y="50" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontFamily="Lexend, sans-serif" fontSize="24" fontWeight="700">Ainous AI Architecture</text>
                    <text x="450" y="550" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontFamily="Lexend, sans-serif" fontSize="14" fontWeight="500">Advanced Neural Networks with Real-time Learning</text>
                  </svg>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;